package com.sddamico.draftmachine.components.pwa

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleMono
import com.sddamico.draftmachine.ds.core.Badge
import com.sddamico.draftmachine.ds.core.BadgeTone
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.core.CardVariant
import com.sddamico.draftmachine.ds.core.Divider
import com.sddamico.draftmachine.ds.feedback.Toast
import com.sddamico.draftmachine.ds.feedback.ToastTone
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.models.PushStatusRequest
import com.sddamico.draftmachine.models.PushStatusResponse
import com.sddamico.draftmachine.models.PushSubscribeRequest
import com.sddamico.draftmachine.models.PushSubscriptionSummary
import com.sddamico.draftmachine.models.PushTestResponse
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.util.BrowserPushSubscription
import com.sddamico.draftmachine.util.PushPermissionDeniedException
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.currentPushSubscription
import com.sddamico.draftmachine.util.ensureServiceWorker
import com.sddamico.draftmachine.util.formatStampDateTime
import com.sddamico.draftmachine.util.isPushSupported
import com.sddamico.draftmachine.util.notificationPermission
import com.sddamico.draftmachine.util.subscribeToPush
import com.sddamico.draftmachine.util.unsubscribeFromPush
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/** What the check concluded, in the order the UI cares about (worst news first). */
private enum class PushHealth {
  Checking,
  Unsupported,      // no service worker / PushManager / Notification in this browser
  ServerOffline,    // the app has no VAPID credentials, so nothing can be delivered
  Blocked,          // the browser denied the notification permission
  NotEnabled,       // nothing subscribed here yet
  Stale,            // subscribed here, but the server's copy is missing, mismatched, or key-rotated
  Connected,
}

/** One line of the diagnostic checklist. */
private enum class SignalState { Ok, Bad, Warn, Unknown }

private class Signal(val label: String, val state: SignalState, val detail: String)

private class PushCheck(
  val health: PushHealth,
  val status: PushStatusResponse? = null,
  val local: BrowserPushSubscription? = null,
  val reason: String? = null,
  val signals: List<Signal> = emptyList(),
)

/**
 * Self-service verification that browser push actually works for this account on this browser.
 * Answers three separate questions the user otherwise can't distinguish: does the browser allow
 * notifications, does the server hold current credentials for this browser, and does a real push
 * make it all the way to the notification tray.
 *
 * Lives on its own page (`/settings/alerts`) rather than in the account card — the check does real
 * network and service-worker work on mount, which shouldn't fire every time someone opens settings.
 */
@Composable
fun PushDiagnosticsPanel() {
  val scope = rememberCoroutineScope()
  var check by remember { mutableStateOf(PushCheck(PushHealth.Checking)) }
  var busy by remember { mutableStateOf(false) }
  var testing by remember { mutableStateOf(false) }
  var message by remember { mutableStateOf<Pair<ToastTone, String>?>(null) }

  suspend fun runCheck() {
    check = PushCheck(PushHealth.Checking)
    if (!isPushSupported()) {
      check = PushCheck(PushHealth.Unsupported, signals = listOf(unsupportedSignal()))
      return
    }
    try {
      ensureServiceWorker()
      val local = currentPushSubscription()
      val status = apiPost(
        "/api/push/status",
        apiJson.encodeToString(
          PushStatusRequest(local?.endpoint, local?.p256dh, local?.auth),
        ),
        PushStatusResponse.serializer(),
      )
      val browser = status.thisBrowser
      val permission = notificationPermission()
      // A rotated server key leaves the browser happily subscribed to a key nobody can sign with.
      val keyRotated = local?.applicationServerKey != null && status.publicKey != null &&
        local.applicationServerKey != status.publicKey
      val health = when {
        !status.configured -> PushHealth.ServerOffline
        permission == "denied" -> PushHealth.Blocked
        local == null || browser == null -> PushHealth.NotEnabled
        !browser.registered || !browser.keysMatch || keyRotated -> PushHealth.Stale
        else -> PushHealth.Connected
      }
      val reason = when {
        health != PushHealth.Stale -> null
        browser?.ownedByAnotherAccount == true -> "This browser's subscription belongs to another account."
        browser?.registered == false -> "This browser isn't registered with the machine."
        browser?.keysMatch == false -> "The stored encryption keys don't match this browser's."
        else -> "The machine's signing credentials were rotated since you signed up."
      }
      check = PushCheck(
        health = health,
        status = status,
        local = local,
        reason = reason,
        signals = buildSignals(status, local, permission, keyRotated),
      )
    } catch (e: ApiException) {
      check = PushCheck(PushHealth.NotEnabled, reason = e.errorMessage)
    } catch (e: Throwable) {
      check = PushCheck(PushHealth.NotEnabled, reason = e.message ?: UNREACHABLE_MESSAGE)
    }
  }

  LaunchedEffect(Unit) { runCheck() }

  /** Subscribe (or re-subscribe from scratch when the old credentials are stale) and re-check. */
  fun connect(fresh: Boolean) {
    if (busy) return
    busy = true
    message = null
    scope.launch {
      try {
        // Preloaded by the check, so there's no network await between the click and subscribe() —
        // Android Chrome drops the user activation across one and push then fails silently.
        val key = check.status?.publicKey
        if (key == null) {
          message = ToastTone.Rust to "The machine has no push credentials to hand out right now."
        } else {
          // subscribe() rejects a new applicationServerKey while an old subscription is live.
          if (fresh) unsubscribeFromPush()
          val subscription = subscribeToPush(key)
          apiPost(
            "/api/push/subscribe",
            apiJson.encodeToString(
              PushSubscribeRequest(subscription.endpoint, subscription.p256dh, subscription.auth),
            ),
            SuccessResponse.serializer(),
          )
          runCheck()
          message = ToastTone.Verde to "This browser is connected. Send a test signal to confirm."
        }
      } catch (e: PushPermissionDeniedException) {
        runCheck()
        message = ToastTone.Rust to "Your browser blocked notifications (${e.permission})."
      } catch (e: ApiException) {
        message = ToastTone.Rust to e.errorMessage
      } catch (e: Throwable) {
        message = ToastTone.Rust to (e.message ?: UNREACHABLE_MESSAGE)
      }
      busy = false
    }
  }

  fun sendTest() {
    if (testing) return
    testing = true
    message = null
    scope.launch {
      try {
        val result = apiPost("/api/push/test", "{}", PushTestResponse.serializer())
        message = describeTest(result)
        // A failed send may have pruned a dead endpoint server-side; re-read so the card agrees.
        if (result.delivered < result.attempted) runCheck()
      } catch (e: ApiException) {
        message = ToastTone.Rust to e.errorMessage
      } catch (e: Throwable) {
        message = ToastTone.Rust to (e.message ?: UNREACHABLE_MESSAGE)
      }
      testing = false
    }
  }

  message?.let { (tone, text) ->
    Toast(
      title = if (tone == ToastTone.Verde) "Signal check" else "Heads up",
      message = text,
      tone = tone,
      onClose = { message = null },
      modifier = Modifier.margin(bottom = DmSpacing.space4),
    )
  }

  Card(variant = CardVariant.Raised, riveted = true) {
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space4).toAttrs()) {
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
          .justifyContent(JustifyContent.SpaceBetween).gap(DmSpacing.space3).toAttrs()
      ) {
        SpanText("Connection", Modifier.dmStamp())
        HealthBadge(check.health)
      }

      SpanText(
        check.reason ?: healthCopy(check.health),
        Modifier.roleBody().fontSize(DmType.textSm).color(DmColor.textSecondary).displayBlock(),
      )

      if (check.health != PushHealth.Checking) {
        Divider()
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.FlexEnd).gap(DmSpacing.space2).toAttrs()
        ) {
          Button(
            "Re-check", variant = ButtonVariant.Ghost, size = ButtonSize.Sm, icon = "arrows-clockwise",
            onClick = { if (!busy) scope.launch { runCheck() } },
          )
          when (check.health) {
            PushHealth.NotEnabled -> Button(
              "Enable alerts", size = ButtonSize.Sm, icon = "bell-ringing", loading = busy,
              onClick = { connect(fresh = false) },
            )
            PushHealth.Stale -> Button(
              "Reconnect", size = ButtonSize.Sm, icon = "plugs-connected", loading = busy,
              onClick = { connect(fresh = true) },
            )
            PushHealth.Connected -> Button(
              "Send test signal", size = ButtonSize.Sm, icon = "paper-plane-tilt", loading = testing,
              onClick = { sendTest() },
            )
            else -> Unit
          }
        }
      }
    }
  }

  if (check.signals.isNotEmpty()) {
    SpanText(
      "Checks",
      Modifier.dmStamp().displayBlock().margin(top = DmSpacing.space6, bottom = DmSpacing.space3),
    )
    Card(variant = CardVariant.Panel) {
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space4).toAttrs()) {
        check.signals.forEach { SignalRow(it) }
      }
    }
  }

  val subscriptions = check.status?.subscriptions.orEmpty()
  if (subscriptions.isNotEmpty()) {
    SpanText(
      "Registered browsers (${subscriptions.size})",
      Modifier.dmStamp().displayBlock().margin(top = DmSpacing.space6, bottom = DmSpacing.space3),
    )
    Card(variant = CardVariant.Panel) {
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space4).toAttrs()) {
        subscriptions.forEachIndexed { index, summary ->
          if (index > 0) Divider()
          SubscriptionRow(summary)
        }
      }
    }
  }
}

private fun unsupportedSignal() = Signal(
  "Browser support",
  SignalState.Bad,
  "This browser has no Push API. On iPhone, add the app to your home screen first.",
)

private fun buildSignals(
  status: PushStatusResponse,
  local: BrowserPushSubscription?,
  permission: String,
  keyRotated: Boolean,
): List<Signal> = listOf(
  Signal("Browser support", SignalState.Ok, "Push API and service worker available."),
  Signal(
    "Notification permission",
    when (permission) {
      "granted" -> SignalState.Ok
      "denied" -> SignalState.Bad
      else -> SignalState.Warn
    },
    when (permission) {
      "granted" -> "Allowed for this site."
      "denied" -> "Blocked — allow notifications in your browser's site settings."
      else -> "Not asked yet."
    },
  ),
  Signal(
    "Machine transmitter",
    if (status.configured) SignalState.Ok else SignalState.Bad,
    if (status.configured) "The server holds signing credentials." else "No VAPID keys configured on the server.",
  ),
  Signal(
    "This browser registered",
    when {
      status.thisBrowser?.registered == true && status.thisBrowser?.keysMatch == true -> SignalState.Ok
      local == null -> SignalState.Warn
      else -> SignalState.Bad
    },
    when {
      status.thisBrowser?.registered == true && status.thisBrowser?.keysMatch == true ->
        "The machine has current keys for this browser."
      local == null -> "No subscription on this browser yet."
      status.thisBrowser?.registered != true -> "Subscribed here, but the machine has no record of it."
      else -> "The machine's stored keys are out of date."
    },
  ),
  Signal(
    "Signing credentials",
    when {
      local == null -> SignalState.Unknown
      keyRotated -> SignalState.Bad
      else -> SignalState.Ok
    },
    when {
      local == null -> "Nothing to compare until this browser subscribes."
      keyRotated -> "This browser subscribed against an older server key — reconnect to refresh it."
      else -> "This browser's subscription matches the machine's current key."
    },
  ),
)

@Composable
private fun SignalRow(signal: Signal) {
  val (glyph, tint) = when (signal.state) {
    SignalState.Ok -> "check-circle" to DmColor.verde400
    SignalState.Bad -> "x-circle" to DmColor.rust400
    SignalState.Warn -> "warning-circle" to DmColor.brass400
    SignalState.Unknown -> "minus-circle" to DmColor.textMuted
  }
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.FlexStart).gap(DmSpacing.space3).toAttrs()) {
    Icon(glyph, IconWeight.Fill, Modifier.fontSize(18.px).color(tint).flex("0 0 auto"))
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(2.px).toAttrs()) {
      SpanText(
        signal.label,
        Modifier.roleBody().fontSize(DmType.textSm).fontWeight(DmType.weightBold)
          .color(DmColor.text).displayBlock(),
      )
      SpanText(
        signal.detail,
        Modifier.roleBody().fontSize(DmType.textXs).color(DmColor.textMuted).displayBlock(),
      )
    }
  }
}

@Composable
private fun SubscriptionRow(summary: PushSubscriptionSummary) {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
      .justifyContent(JustifyContent.SpaceBetween).gap(DmSpacing.space3).toAttrs()
  ) {
    Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(2.px).toAttrs()) {
      SpanText(
        summary.provider,
        Modifier.roleMono().fontSize(DmType.textXs).color(DmColor.text).displayBlock(),
      )
      SpanText(
        "Registered ${formatStampDateTime(summary.registeredAt)}",
        Modifier.roleBody().fontSize(DmType.text2xs).color(DmColor.textMuted).displayBlock(),
      )
    }
    if (summary.isThisBrowser) Badge("This browser", tone = BadgeTone.Glass, dot = true)
  }
}

@Composable
private fun HealthBadge(health: PushHealth) = when (health) {
  PushHealth.Checking -> Badge("Checking", tone = BadgeTone.Neutral)
  PushHealth.Unsupported -> Badge("Unsupported", tone = BadgeTone.Neutral)
  PushHealth.ServerOffline -> Badge("Transmitter off", tone = BadgeTone.Rust, dot = true)
  PushHealth.Blocked -> Badge("Blocked", tone = BadgeTone.Rust, dot = true)
  PushHealth.NotEnabled -> Badge("Not enabled", tone = BadgeTone.Brass, dot = true)
  PushHealth.Stale -> Badge("Needs repair", tone = BadgeTone.Brass, dot = true)
  PushHealth.Connected -> Badge("Connected", tone = BadgeTone.Verde, dot = true)
}

private fun healthCopy(health: PushHealth): String = when (health) {
  PushHealth.Checking -> "Reading the gauges…"
  PushHealth.Unsupported ->
    "This browser doesn't support web push. On iPhone, add the app to your home screen first."
  PushHealth.ServerOffline ->
    "The machine has no push credentials configured, so nothing can be delivered right now."
  PushHealth.Blocked ->
    "Notifications are blocked for this site. Allow them in your browser's site settings, then re-check."
  PushHealth.NotEnabled -> "This browser isn't set up for alerts yet."
  PushHealth.Stale -> "The stored credentials are out of date — reconnect to repair them."
  PushHealth.Connected ->
    "Wired up. A test signal should surface as a notification within a few seconds."
}

private fun describeTest(result: PushTestResponse): Pair<ToastTone, String> = when {
  result.attempted == 0 -> ToastTone.Rust to "No browsers are registered to your account."
  result.delivered == result.attempted ->
    ToastTone.Verde to "Sent to ${result.attempted} browser${if (result.attempted == 1) "" else "s"}. " +
      "Watch for the notification — if it never appears, your operating system is suppressing it."
  else -> {
    val detail = result.failures.joinToString("; ") {
      "${it.provider}: ${it.detail}${if (it.removed) " (dropped)" else ""}"
    }
    ToastTone.Rust to "Delivered to ${result.delivered} of ${result.attempted}. $detail"
  }
}
